import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { OrganizationDto } from '../dto/organization.dto';
import { FeedPost } from './mock-feed.service';
import { Job } from './mock-job.service';

export interface OrganizationMetrics {
  connectionsByMonth: { month: string; count: number }[];
  playsByMonth: { month: string; count: number }[];
  appliesByMonth: { month: string; count: number }[];
  postsByCategory: { category: string; count: number }[];
}

@Injectable({
  providedIn: 'root',
})
export class MockOrganizationService {
  private mockOrganizations: OrganizationDto[] = [
    {
      id: 'org-summit-construction',
      name: 'Summit Construction Group',
      description:
        'Leading construction company specializing in commercial and residential projects. We are committed to excellence and innovation in every project we undertake.',
      initials: 'SC',
      location: 'Denver, CO',
      website: 'https://summitconstruction.com',
      followersCount: 1250,
      followingCount: 45,
      createdAt: new Date('2020-01-15'),
      updatedAt: new Date('2024-12-01'),
    },
    {
      id: 'org-buildright',
      name: 'BuildRight Contractors',
      description:
        'Award-winning construction firm focused on sustainable building practices and green construction. We build for the future.',
      initials: 'BR',
      location: 'Portland, OR',
      website: 'https://buildright.com',
      followersCount: 980,
      followingCount: 32,
      createdAt: new Date('2019-03-20'),
      updatedAt: new Date('2024-11-15'),
    },
    {
      id: 'org-healthfirst',
      name: 'HealthFirst Medical Systems',
      description:
        'Comprehensive healthcare provider network delivering quality medical care to communities across the region.',
      initials: 'HF',
      location: 'Boston, MA',
      website: 'https://healthfirst.com',
      avatar: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400',
      followersCount: 2100,
      followingCount: 78,
      createdAt: new Date('2018-06-10'),
      updatedAt: new Date('2024-12-10'),
    },
    {
      id: 'org-cloudsync',
      name: 'CloudSync Technologies',
      description:
        'Enterprise cloud solutions provider helping businesses transform their operations with cutting-edge technology.',
      initials: 'CS',
      location: 'Seattle, WA',
      website: 'https://cloudsync.com',
      followersCount: 1850,
      followingCount: 56,
      createdAt: new Date('2017-09-05'),
      updatedAt: new Date('2024-12-05'),
    },
    {
      id: 'org-metro-builders',
      name: 'Metro Builders LLC',
      description:
        'Full-service construction company specializing in commercial developments and mixed-use facilities.',
      initials: 'MB',
      location: 'Chicago, IL',
      website: 'https://metrobuilders.com',
      followersCount: 750,
      followingCount: 28,
      createdAt: new Date('2021-02-14'),
      updatedAt: new Date('2024-11-20'),
    },
    {
      id: 'org-precision-health',
      name: 'Precision Health Partners',
      description:
        'Advanced medical practice specializing in precision medicine and personalized treatment plans.',
      initials: 'PH',
      location: 'San Diego, CA',
      website: 'https://precisionhealth.com',
      followersCount: 1420,
      followingCount: 41,
      createdAt: new Date('2019-11-08'),
      updatedAt: new Date('2024-12-08'),
    },
    {
      id: 'org-codecraft',
      name: 'CodeCraft Software',
      description:
        'Software development company creating innovative developer tools and platforms to enhance productivity.',
      initials: 'CC',
      location: 'Austin, TX',
      website: 'https://codecraft.com',
      followersCount: 1650,
      followingCount: 62,
      createdAt: new Date('2016-04-22'),
      updatedAt: new Date('2024-12-15'),
    },
    {
      id: 'org-techvantage',
      name: 'TechVantage Solutions',
      description:
        'IT consulting and solutions provider helping businesses leverage technology for digital transformation.',
      initials: 'TV',
      location: 'San Francisco, CA',
      website: 'https://techvantage.com',
      avatar: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400',
      followersCount: 1920,
      followingCount: 71,
      createdAt: new Date('2015-08-30'),
      updatedAt: new Date('2024-12-12'),
    },
    {
      id: 'org-medcare',
      name: 'MedCare Innovations',
      description:
        'Healthcare technology company developing telemedicine platforms and digital health solutions.',
      initials: 'MI',
      location: 'New York, NY',
      website: 'https://medcare.com',
      avatar: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400',
      followersCount: 2300,
      followingCount: 89,
      createdAt: new Date('2014-12-03'),
      updatedAt: new Date('2024-12-18'),
    },
    {
      id: 'org-digital-dynamics',
      name: 'Digital Dynamics Inc.',
      description:
        'AI and analytics company providing data-driven solutions for enterprise clients.',
      initials: 'DD',
      location: 'Los Angeles, CA',
      website: 'https://digitaldynamics.com',
      followersCount: 1100,
      followingCount: 38,
      createdAt: new Date('2020-07-19'),
      updatedAt: new Date('2024-11-25'),
    },
  ];

  getOrganization(id: string): Observable<OrganizationDto> {
    const organization = this.mockOrganizations.find((org) => org.id === id);
    if (!organization) {
      throw new Error(`Organization with id ${id} not found`);
    }
    return of(organization).pipe(delay(200));
  }

  getOrganizationPosts(organizationId: string): Observable<FeedPost[]> {
    // This would be implemented to get posts from the feed service filtered by organization
    // For now, return empty array - will be populated by the component
    return of([]).pipe(delay(200));
  }

  getOrganizationJobs(organizationId: string): Observable<Job[]> {
    // This would be implemented to get jobs from the job service filtered by organization
    // For now, return empty array - will be populated by the component
    return of([]).pipe(delay(200));
  }

  getOrganizationMetrics(organizationId: string): Observable<OrganizationMetrics> {
    // Generate mock metrics data
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Generate random but realistic data
    const generateMonthlyData = (base: number, variance: number) => {
      return months.map((month) => ({
        month,
        count: Math.floor(base + (Math.random() - 0.5) * variance),
      }));
    };

    const metrics: OrganizationMetrics = {
      connectionsByMonth: generateMonthlyData(45, 20),
      playsByMonth: generateMonthlyData(120, 40),
      appliesByMonth: generateMonthlyData(25, 15),
      postsByCategory: [
        { category: 'Construction', count: Math.floor(Math.random() * 20) + 10 },
        { category: 'Medical', count: Math.floor(Math.random() * 15) + 5 },
        { category: 'IT', count: Math.floor(Math.random() * 25) + 15 },
        { category: 'Manufacturing', count: Math.floor(Math.random() * 10) + 3 },
        { category: 'Finance', count: Math.floor(Math.random() * 8) + 2 },
        { category: 'Education', count: Math.floor(Math.random() * 12) + 4 },
      ],
    };

    return of(metrics).pipe(delay(300));
  }
}

